import './styles.scss';
import './sph.css';
import App from './App.vue';
import { APP_CONFIG } from './config';
import { vCatchAppLinks } from '@shared/directives/catchAppLinks';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { i18n } from '@shared/i18n';
import { router } from './router';
import { migrateLegacyAuthStorage, useUserStore } from '@shared/stores/user';
import { notifyCapgoAppReady } from '@shared/composables/useCapgo';
import { initAppearance } from '@shared/composables/useAppearance';
import { initTheme } from '@shared/composables/useTheme';

// Confirm the running bundle to Capgo before anything that touches the network — the mount below
// waits for the locale catalogue, which is far too late for the 10s appReadyTimeout.
if (window.kgrCapacitor) {
    void notifyCapgoAppReady();
}

initAppearance();
initTheme();

axios.interceptors.request.use((config) => {
    const { apiHost } = window.kgrDynamicConfig;
    if (config.url && !config.url.startsWith('http')) {
        config.url = `${apiHost}/api${config.url}`;
    }
    const userStore = useUserStore();
    if (userStore.token) {
        config.headers.set('Authorization', `Bearer ${userStore.token}`);
    }
    return config;
});

window.kgrDynamicConfig = {
    ...window.kgrDynamicConfig,
    apiHost: window.kgrDynamicConfig?.apiHost || window.location.origin,
    apiPath: window.kgrDynamicConfig?.apiPath || APP_CONFIG.apiPath,
    appVersion: window.kgrDynamicConfig?.appVersion || APP_CONFIG.appVersion,
    appId: APP_CONFIG.appId,
    appType: APP_CONFIG.appType as 'employee' | 'end_user',
    institutionType: APP_CONFIG.institutionType as 'kindergarten' | 'club',
    sentryDsn: window.kgrDynamicConfig?.sentryDsn ?? null,
    sentryEnvironment: APP_CONFIG.sentryEnvironment,
    clientLogoFileName: window.kgrDynamicConfig?.clientLogoFileName ?? APP_CONFIG.clientLogoFileName,
    clientLogoInMenuFileName: window.kgrDynamicConfig?.clientLogoInMenuFileName,
    // Angular merget `...clientEnvironment` zuletzt, der Client-Wert gewinnt also. Vier
    // club-member-app-Clients (ljvb, ljv-mv, norddeutscher-regatta-verein, tsg-heilbronn) setzen
    // `staticLogo: true`; ein fester APP_CONFIG-Wert hätte den stillschweigend verworfen.
    staticLogo: window.kgrDynamicConfig?.staticLogo ?? APP_CONFIG.staticLogo,
    privacyUrl: window.kgrDynamicConfig?.privacyUrl ?? APP_CONFIG.privacyUrl,
    imprintUrl: window.kgrDynamicConfig?.imprintUrl ?? null,
    // Per white-label client (native multi-tenant): set via dynamic.config.js. `organization`
    // gates isCustomizedApp; `federationIds` scopes which federations may log in (KgrLoginForm);
    // `welcomeMessage` is the login-screen greeting (locale-keyed); `organizationProfile` is the
    // client's own org (incl. federationProfile) shown pre-login on the login screen and the public
    // imprint/contact (= Angular environment.client `organization`).
    organization: window.kgrDynamicConfig?.organization ?? APP_CONFIG.organization,
    federationIds: window.kgrDynamicConfig?.federationIds ?? APP_CONFIG.federationIds,
    welcomeMessage: window.kgrDynamicConfig?.welcomeMessage ?? null,
    organizationProfile: window.kgrDynamicConfig?.organizationProfile ?? null,
    appUrlRegex: APP_CONFIG.appUrlRegex,
    platformOrganization: APP_CONFIG.platformOrganization,
};

const app = createApp(App);

const { sentryDsn, sentryEnvironment } = window.kgrDynamicConfig;
if (sentryDsn && sentryEnvironment) {
    Sentry.init({
        app,
        dsn: sentryDsn,
        environment: sentryEnvironment,
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        tracesSampleRate: 0.05,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0.05,
        ignoreErrors: [/^Http failure response for (.*): 0 Unknown Error$/],
    });
    Sentry.setTag('runtime', window.kgrCapacitor ? 'wrapapp' : 'webapp');
    Sentry.setTag('webAppId', APP_CONFIG.appId);
    Sentry.setTag('webAppVersion', window.kgrDynamicConfig.appVersion);
}

// A device updating from the Angular build keeps its session in @capacitor/preferences, not in
// localStorage — migrate it before the router is installed, because `app.use(router)` starts the
// initial navigation and its guard would bounce an actually-valid session to the login screen.
migrateLegacyAuthStorage()
    .catch((error) => console.error('[boot] legacy auth migration failed', error))
    .then(() => {
        app.use(createPinia()).use(router).use(i18n).directive('catch-app-links', vCatchAppLinks);
        return router.isReady();
    })
    .catch((error) => console.error('[boot] initial navigation failed', error))
    // Mount only after the initial navigation resolved: the `beforeEach` guard awaits
    // `loadLocale(...)` there, so the locale catalog is in place before the first render.
    // Otherwise the shell renders its `$t()` keys against an empty `de` catalog → intlify
    // "Not found key" warnings for translations that actually exist.
    // `.catch` is a safety net: a rejected initial navigation (failing guard) must never leave the
    // app unmounted (blank/blue screen) — mount regardless so the UI renders.
    .finally(() => app.mount('#app'));
